import { localDateKey } from './timezones';
import type { LocalDay } from '../components/booking-ui';
import type { AvailabilityDay } from '../hooks/use-public-booking';

/**
 * Redo the API's grouping in the zone the page is showing.
 *
 * The API groups slots under the BRISBANE date, because that is the day the host's
 * working hours belong to. Rendered in another zone the times move, and once the times
 * move the day boundaries move with them. A 9am Brisbane slot is the evening before in
 * London; an evening Brisbane slot is the same morning in Auckland. Keeping Brisbane's
 * grouping and labelling the tiles with the visitor's dates would file slots under a day
 * they are not on, which reads as a bug and books the wrong meeting.
 *
 * Empty days are dropped, the way the page has always dropped them. Somebody scanning a
 * QR code from their seat should not be offered a tile with nothing behind it.
 *
 * The tile's label is taken from the first slot of the day rather than from the date key,
 * because the key is only a string and the label has to be rendered in the same zone the
 * times are.
 */
export function groupByLocalDay(days: AvailabilityDay[] | undefined, zone: string): LocalDay[] {
  if (!days?.length) return [];

  const byDate = new Map<string, LocalDay>();
  for (const day of days) {
    for (const slot of day.slots) {
      const key = localDateKey(slot.starts_at, zone);
      const existing = byDate.get(key);
      if (existing) {
        existing.slots.push(slot);
        existing.labelAt = Math.min(existing.labelAt, slot.starts_at);
      } else {
        byDate.set(key, { date: key, labelAt: slot.starts_at, slots: [slot] });
      }
    }
  }

  const out = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  for (const day of out) day.slots.sort((a, b) => a.starts_at - b.starts_at);
  return out;
}
